import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Mail, Phone, Cake, Landmark, Eye, EyeOff, Pencil,
  ChevronDown, ChevronUp, ChevronRight, Home, MapPin,
} from "lucide-react";
import { fileUrl } from "../../lib/api";
import Avatar from "../Avatar";
import { BRAND_COLORS } from "../../lib/bookingStatus";
import { deDayMonth } from "../../lib/dateFormat";

/**
 * Feb 2026 · Read-only Kunden-Profil-Header für die Admin-/Concierge-
 * Kunden-Detail-Ansicht.
 *
 * Enthält alle „stammdaten-artigen" Angaben in einer klaren Read-Only-
 * Karte damit der Admin nicht versehentlich Felder editiert:
 *   - Foto + Name + Willkommens-Alias
 *   - E-Mail (mailto) + Telefon (tel)
 *   - Geburtstag (formatiert `TT. Monat`)
 *   - Service-Tier (Membership-Badge)
 *   - Zugewiesene Objekte (aufklappbar: Zeile mit Zähler → Liste)
 *   - Bankverbindung (IBAN standardmäßig maskiert, Klick → volle Anzeige)
 *
 * Änderungen laufen ausschließlich über den „Bearbeiten"-Button, der
 * `CustomerProfileEditModal` öffnet und `PATCH /team/customers/{id}/profile`
 * aufruft. Der Admin bekommt so eine klar getrennte Ansehen-/Ändern-Trennung.
 */

const DE_BDAY = (iso) => {
  if (!iso) return null;
  try {
    return deDayMonth(iso);
  } catch (e) { return null; }
};

// Feb 2026 · Directive #294 · Anzeige-Labels für das Servicepaket im Header.
// Backend-Werte bleiben englisch (essential/plus/signature).

/**
 * Kleine, saubere SVG-Flaggen (24×18 px) für die Sprach-Anzeige im Header.
 * Bewusst SVG statt Emoji: Emoji-Rendering ist plattformabhängig und passt
 * nicht zum ruhigen Weissblom-Ton. Die Flaggen sind rein dekorativ + haben
 * ein `title` am umschließenden Element für Screen-Reader-Feedback.
 */
function LanguageFlag({ lang }) {
  if ((lang || "de") === "en") {
    return (
      <svg
        width="24" height="18"
        viewBox="0 0 60 30"
        xmlns="http://www.w3.org/2000/svg"
        data-testid="customer-profile-language-flag-en"
        className="rounded-sm border border-navy/15 shadow-sm"
        aria-hidden="true"
      >
        <clipPath id="wb-uk-clip"><rect width="60" height="30" /></clipPath>
        <clipPath id="wb-uk-diag">
          <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
        </clipPath>
        <g clipPath="url(#wb-uk-clip)">
          <rect width="60" height="30" fill="#012169" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
          <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#wb-uk-diag)"
                stroke="#C8102E" strokeWidth="4" />
          <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
          <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
        </g>
      </svg>
    );
  }
  // Default: Deutschland (schwarz / rot / gold)
  return (
    <svg
      width="24" height="18"
      viewBox="0 0 5 3"
      xmlns="http://www.w3.org/2000/svg"
      data-testid="customer-profile-language-flag-de"
      className="rounded-sm border border-navy/15 shadow-sm"
      aria-hidden="true"
    >
      <rect width="5" height="1" y="0" fill="#000" />
      <rect width="5" height="1" y="1" fill="#DD0000" />
      <rect width="5" height="1" y="2" fill="#FFCE00" />
    </svg>
  );
}

/**
 * Maskiert eine IBAN: erste 4 Zeichen + „•••• •••• •••• 3000" (letzte 4).
 * Falls IBAN zu kurz, wird sie 1:1 zurückgegeben (Editor sollte 15+ Zeichen
 * erzwingen, aber wir vertrauen der Datenbank nicht blind).
 */
const maskIban = (iban) => {
  if (!iban) return "";
  const clean = iban.replace(/\s+/g, "").toUpperCase();
  if (clean.length < 8) return clean;
  const head = clean.slice(0, 4);
  const tail = clean.slice(-4);
  return `${head} •••• •••• •••• ${tail}`;
};

const formatIban = (iban) => {
  if (!iban) return "";
  const clean = iban.replace(/\s+/g, "").toUpperCase();
  return clean.match(/.{1,4}/g)?.join(" ") || clean;
};

export function CustomerProfileHeader({
  customer,
  properties = [],
  canEdit,
  onEditClick,
}) {
  const [ibanRevealed, setIbanRevealed] = useState(false);
  const [propertiesOpen, setPropertiesOpen] = useState(false);

  const displayName = customer.name || customer.email;
  const birthdayLabel = useMemo(() => DE_BDAY(customer.birthday), [customer.birthday]);
  const hasBank = !!(
    customer.bank_iban || customer.bank_name ||
    customer.bank_bic || customer.bank_account_holder
  );

  return (
    <div
      className="mb-6 bg-white border border-navy/10 p-5 md:p-6"
      data-testid="customer-profile-header"
    >
      {/* Header row: Foto + Titel + Bearbeiten */}
      <div className="flex items-start gap-5 flex-wrap">
        <div className="shrink-0">
          {/* Feb 2026 · Directive #295: Profilfoto liegt jetzt direkt im
              quadratischen Rahmen — kein runder Kreis-Overlay mehr. Priorität:
              (1) `avatar_file_id` (echtes Kunden-Profilfoto), (2) `hero_image_url`
              (Objekt-Hero als Fallback für Kunden ohne Foto), (3) Platzhalter. */}
          {customer.avatar_file_id ? (
            <img
              src={fileUrl(customer.avatar_file_id)}
              alt={`${displayName} · Profilfoto`}
              data-testid="customer-detail-avatar"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="w-20 h-20 md:w-24 md:h-24 object-cover border border-navy/10"
            />
          ) : customer.hero_image_url ? (
            <img
              src={customer.hero_image_url}
              alt={displayName || ""}
              className="w-20 h-20 md:w-24 md:h-24 object-cover border border-navy/10"
              data-testid="customer-hero"
            />
          ) : displayName ? (
            <span data-testid="customer-detail-avatar-fallback">
              <Avatar name={displayName} size={96} />
            </span>
          ) : (
            <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center border border-dashed border-navy/20 bg-ivory/50 label-caps text-navy-dark/40">
              —
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="label-caps mb-1">Kundenprofil</div>
              <h1
                className="font-display text-3xl md:text-4xl text-navy leading-tight"
                data-testid="customer-name"
              >
                {displayName}
              </h1>
              {customer.welcome_name && (
                <div
                  className="text-[14px] italic mt-1"
                  style={{ color: BRAND_COLORS.goldDark }}
                >
                  Willkommen: „{customer.welcome_name}"
                </div>
              )}
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={onEditClick}
                data-testid="customer-profile-edit-btn"
                className="inline-flex items-center gap-1.5 border border-navy/25 hover:border-gold hover:text-gold-dark px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase text-navy bg-white transition-colors"
                title="Kundenprofil bearbeiten"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
                Bearbeiten
              </button>
            )}
          </div>

          {/* Kontakt-Zeile · read-only */}
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-navy-dark/75">
            <a
              href={`mailto:${customer.email}`}
              className="inline-flex items-center gap-1.5 hover:text-navy transition-colors"
              data-testid="customer-profile-email"
            >
              <Mail className="w-3.5 h-3.5" strokeWidth={1.6} />
              {customer.email}
            </a>
            {customer.phone ? (
              <a
                href={`tel:${customer.phone.replace(/\s+/g, "")}`}
                className="inline-flex items-center gap-1.5 hover:text-navy transition-colors"
                data-testid="customer-profile-phone"
              >
                <Phone className="w-3.5 h-3.5" strokeWidth={1.6} />
                {customer.phone}
              </a>
            ) : (
              <span
                className="inline-flex items-center gap-1.5 italic text-navy-dark/40"
                data-testid="customer-profile-phone-empty"
              >
                <Phone className="w-3.5 h-3.5" strokeWidth={1.6} />
                Kein Telefon
              </span>
            )}
            <span
              className="inline-flex items-center gap-1.5"
              data-testid="customer-profile-birthday"
            >
              <Cake className="w-3.5 h-3.5" strokeWidth={1.6} />
              {birthdayLabel || (
                <span className="italic text-navy-dark/40">Kein Geburtstag</span>
              )}
            </span>
            <span
              className="inline-flex items-center"
              data-testid="customer-profile-language"
              title={customer.language === "en" ? "Sprache: Englisch" : "Sprache: Deutsch"}
              aria-label={customer.language === "en" ? "Sprache Englisch" : "Sprache Deutsch"}
            >
              <LanguageFlag lang={customer.language} />
            </span>
          </div>
        </div>
      </div>

      {/* Zugewiesene Objekte — aufklappbare Zeile */}
      <div className="mt-5 pt-4 border-t border-navy/10">
        <button
          type="button"
          onClick={() => setPropertiesOpen((x) => !x)}
          data-testid="customer-profile-properties-toggle"
          className="w-full flex items-center justify-between gap-2 text-left group"
          aria-expanded={propertiesOpen}
        >
          <div className="inline-flex items-center gap-2 text-[13px] text-navy-dark/80">
            <Home className="w-3.5 h-3.5" strokeWidth={1.8} />
            <span className="label-caps">Zugewiesene Objekte</span>
            <span
              className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-semibold text-ivory"
              style={{ background: BRAND_COLORS.navy }}
              data-testid="customer-profile-properties-count"
            >
              {properties.length}
            </span>
          </div>
          <span className="text-navy-dark/40 group-hover:text-navy transition-colors">
            {propertiesOpen ? (
              <ChevronUp className="w-4 h-4" strokeWidth={1.8} />
            ) : (
              <ChevronDown className="w-4 h-4" strokeWidth={1.8} />
            )}
          </span>
        </button>
        {propertiesOpen && (
          <ul
            className="mt-3 space-y-2"
            data-testid="customer-profile-properties-list"
          >
            {properties.length === 0 ? (
              <li className="text-[12.5px] italic text-navy-dark/50">
                Diesem Kunden ist derzeit kein Objekt zugeordnet.
              </li>
            ) : (
              properties.map((p) => {
                // Feb 2026 · 9 Sep 2026 · User-Directive „Rechnungsadresse
                // eindeutig kennzeichnen": Objekt-Adresse (physisch) und
                // Rechnungsadresse (Fakturierung) stehen jetzt in ZWEI
                // klar beschrifteten Zeilen mit eigenem Badge. Fehlt eine
                // Objekt-Rechnungsadresse, zeigen wir ausdrücklich
                // „Keine Rechnungsadresse hinterlegt" — damit der Admin
                // beim Rechnungserstellen sofort weiß, dass der Kunden-
                // Standard einspringt.
                const propBa = p.billing_address;
                const physical = [
                  p.address,
                  `${p.zip || ""} ${p.city || ""}`.trim(),
                  p.country,
                ].filter((s) => s && String(s).trim()).join(" · ");
                const billingText = propBa
                  ? [
                      propBa.street,
                      `${propBa.zip || ""} ${propBa.city || ""}`.trim(),
                      propBa.country,
                    ].filter((s) => s && String(s).trim()).join(" · ")
                  : "";
                return (
                  <li
                    key={p.id}
                    data-testid={`customer-profile-property-${p.id}`}
                    className="border-l-2 border-navy/10 hover:border-gold transition-colors"
                  >
                    <Link
                      to={`/team/properties/${p.id}`}
                      className="group flex items-start gap-2 text-[13px] text-navy-dark/80 hover:bg-ivory/40 pl-3 py-1 transition-colors"
                      title={`Zur Detailansicht von „${p.name || p.address || "Objekt"}"`}
                    >
                      <MapPin
                        className="w-3.5 h-3.5 mt-0.5 shrink-0"
                        strokeWidth={1.6}
                        style={{ color: BRAND_COLORS.gold }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-navy group-hover:text-gold-dark transition-colors">
                          {p.name || p.property_name || p.address || "Objekt"}
                        </div>
                      </div>
                      <ChevronRight
                        className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-40 group-hover:opacity-100 group-hover:text-gold-dark transition-all"
                        strokeWidth={1.8}
                      />
                    </Link>
                    <div className="pl-9 pb-2 pt-1 space-y-1 text-[11.5px] text-navy-dark/70 leading-relaxed">
                      {/* Objekt-Adresse — physische Adresse des Objekts */}
                      <div
                        className="flex items-start gap-2"
                        data-testid={`customer-profile-property-address-${p.id}`}
                      >
                        <span
                          className="inline-block px-1.5 py-0.5 text-[9.5px] tracking-[0.14em] uppercase font-semibold text-ivory shadow-sm mt-0.5 shrink-0"
                          style={{ background: "rgba(11,42,64,0.55)" }}
                          title="Physische Adresse des Objekts"
                        >
                          Objekt-Adresse
                        </span>
                        <span>{physical || <span className="italic text-navy-dark/40">Keine Adresse hinterlegt</span>}</span>
                      </div>
                      {/* Rechnungsadresse — objektspezifische Fakturierungsadresse */}
                      <div
                        className="flex items-start gap-2"
                        data-testid={`customer-profile-property-billing-${p.id}`}
                      >
                        <span
                          className="inline-block px-1.5 py-0.5 text-[9.5px] tracking-[0.14em] uppercase font-semibold text-ivory shadow-sm mt-0.5 shrink-0"
                          style={{ background: BRAND_COLORS.gold }}
                          title="Objektspezifische Rechnungsadresse — wird bei Rechnungen zu diesem Objekt automatisch übernommen."
                        >
                          Rechnungsadresse
                        </span>
                        {billingText ? (
                          <span data-testid={`customer-profile-property-billing-value-${p.id}`}>{billingText}</span>
                        ) : (
                          <span
                            className="italic text-navy-dark/45"
                            data-testid={`customer-profile-property-billing-empty-${p.id}`}
                          >
                            Keine Rechnungsadresse hinterlegt
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>

      {/* Bankverbindung — read-only mit Maskierung */}
      {hasBank && (
        <div className="mt-4 pt-4 border-t border-navy/10" data-testid="customer-profile-bank">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div className="inline-flex items-center gap-2 label-caps text-navy-dark/80">
              <Landmark className="w-3.5 h-3.5" strokeWidth={1.8} />
              Bankverbindung
            </div>
            {customer.bank_iban && (
              <button
                type="button"
                onClick={() => setIbanRevealed((x) => !x)}
                data-testid="customer-profile-bank-toggle"
                className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy transition-colors"
                aria-pressed={ibanRevealed}
              >
                {ibanRevealed ? (
                  <>
                    <EyeOff className="w-3.5 h-3.5" strokeWidth={1.8} />
                    Verbergen
                  </>
                ) : (
                  <>
                    <Eye className="w-3.5 h-3.5" strokeWidth={1.8} />
                    Anzeigen
                  </>
                )}
              </button>
            )}
          </div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px]">
            {customer.bank_account_holder && (
              <div>
                <dt className="text-[11px] tracking-widest uppercase text-navy-dark/45">
                  Kontoinhaber
                </dt>
                <dd
                  className="text-navy-dark/85"
                  data-testid="customer-profile-bank-holder"
                >
                  {customer.bank_account_holder}
                </dd>
              </div>
            )}
            {customer.bank_name && (
              <div>
                <dt className="text-[11px] tracking-widest uppercase text-navy-dark/45">
                  Bank
                </dt>
                <dd
                  className="text-navy-dark/85"
                  data-testid="customer-profile-bank-name"
                >
                  {customer.bank_name}
                </dd>
              </div>
            )}
            {customer.bank_iban && (
              <div>
                <dt className="text-[11px] tracking-widest uppercase text-navy-dark/45">
                  IBAN
                </dt>
                <dd
                  className="text-navy-dark/85 font-mono tracking-wider"
                  data-testid="customer-profile-bank-iban"
                >
                  {ibanRevealed ? formatIban(customer.bank_iban) : maskIban(customer.bank_iban)}
                </dd>
              </div>
            )}
            {customer.bank_bic && (
              <div>
                <dt className="text-[11px] tracking-widest uppercase text-navy-dark/45">
                  BIC
                </dt>
                <dd
                  className="text-navy-dark/85 font-mono tracking-wider"
                  data-testid="customer-profile-bank-bic"
                >
                  {customer.bank_bic}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}
